import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { useCreateSkillCategory, useUpdateSkillCategory } from '../../hooks/use-skill-categories';
import {
  skillCategoryFormSchema,
  SkillCategoryFormValues,
} from '../../schemas/skill-category-form-schema';
import { SkillCategory } from '../../types/skill-category.types';

export interface SkillCategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skillCategory?: SkillCategory | null;
}

export const SkillCategoryFormDialog = ({
  open,
  onOpenChange,
  skillCategory,
}: SkillCategoryFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(skillCategory);

  const { mutateAsync: createSkillCategory, isPending: isCreating } = useCreateSkillCategory();
  const { mutateAsync: updateSkillCategory, isPending: isUpdating } = useUpdateSkillCategory();
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<SkillCategoryFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(skillCategoryFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: { Title: '' },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({ Title: skillCategory?.Title ?? '' });
  }, [open, skillCategory, form]);

  const handleSubmit = async (values: SkillCategoryFormValues) => {
    try {
      if (isEditMode && skillCategory) {
        await updateSkillCategory({ itemId: skillCategory.ItemId, input: values });
      } else {
        await createSkillCategory(values);
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? t('EDIT_SKILL_CATEGORY') : t('CREATE_SKILL_CATEGORY')}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="Title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('TITLE')} *</FormLabel>
                  <FormControl>
                    <Input placeholder={t('TITLE')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
